// ╔══════════════════════════════════════════════════════════════╗
// ║  Admin/Acc Dashboard — clickable KPIs + charts + production   ║
// ║  Eng role gets the dedicated EngDashboard component           ║
// ╚══════════════════════════════════════════════════════════════╝

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { fmtAR, fmtMoney } from '../lib/format.js';
import { Badge, Card, KPI } from '../components/ui.jsx';
import { BarChart, DonutChart, LineChart } from '../components/Charts.jsx';
import Icon from '../components/Icon.jsx';
import EngDashboard from './EngDashboard.jsx';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Engineering manager has a production-focused dashboard
  if (user?.role === 'eng') return <EngDashboard />;

  // ─── Queries ───
  const { data: dash } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: () => api.get('/reports/dashboard').then((r) => r.data),
  });
  const { data: byDept } = useQuery({
    queryKey: ['reports', 'payroll-by-dept'],
    queryFn: () => api.get('/reports/payroll-by-dept').then((r) => r.data),
  });
  const { data: trend } = useQuery({
    queryKey: ['reports', 'attendance-trend'],
    queryFn: () => api.get('/reports/attendance-trend', { params: { days: 14 } }).then((r) => r.data),
  });
  const { data: eng } = useQuery({
    queryKey: ['reports', 'eng-dashboard'],
    queryFn: () => api.get('/reports/eng-dashboard').then((r) => r.data),
  });
  const { data: prodCost } = useQuery({
    queryKey: ['reports', 'production-cost'],
    queryFn: () => api.get('/reports/production-cost').then((r) => r.data),
  });

  if (!dash) {
    return (
      <div className="p-6 lg:p-8">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-4 h-28 animate-pulse">
              <div className="h-3 bg-ink-100 dark:bg-ink-800 rounded w-20 mb-2" />
              <div className="h-7 bg-ink-100 dark:bg-ink-800 rounded w-16" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const total = dash.employees?.total ?? 0;
  const active = dash.employees?.active ?? 0;
  const present = dash.attendanceToday?.present ?? 0;
  const late = dash.attendanceToday?.late ?? 0;
  const missing = dash.attendanceToday?.missing ?? 0;
  const absent = Math.max(0, active - present);
  const payroll = dash.payroll;
  const totalPayroll = payroll ? Number(payroll.totalNet) : 0;

  const prod = eng?.kpis;
  const prodEff = prod?.efficiency ?? 0;

  // ─── Render ───
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
          لوحة التحكّم التنفيذية
        </div>
        <h1 className="text-2xl md:text-3xl font-black mt-1">
          {greeting()}، <span className="text-brand-500">{user?.name?.split(' ').slice(1, 3).join(' ')}</span>
        </h1>
        <p className="text-sm text-ink-400 mt-1">
          ملخّص اليوم · {new Date().toLocaleDateString('ar-EG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* ─── Production cost cards (Phase I) ─── */}
      {prodCost && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CostCard
            label="تكلفة الإنتاج اليومية"
            value={prodCost.totals?.stage?.daily ?? 0}
            sub={`مجموع رواتب عمال الإنتاج / 30 يوم · ${fmtAR(eng?.kpis?.totalWorkers ?? 0)} عامل`}
            icon="factory"
            tone="brand"
          />
          <CostCard
            label="تكلفة الإنتاج الشهرية"
            value={prodCost.totals?.stage?.monthly ?? 0}
            sub="إجمالى أجور عمال الإنتاج لشهر كامل"
            icon="wallet"
            tone="dark"
          />
        </div>
      )}

      {/* ─── KPIs (6 clickable cards) ─── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <ClickableKPI
          label="إجمالي الموظفين"
          value={fmtAR(total)}
          sub={`${fmtAR(active)} نشط`}
          icon="users"
          accent
          onClick={() => navigate('/employees')}
          foot={<Badge tone="brand" dot>جميع الفروع</Badge>}
        />
        <ClickableKPI
          label="الحاضرون اليوم"
          value={fmtAR(present)}
          sub={active > 0 ? `${Math.round((present / active) * 100)}% من النشطين` : '—'}
          icon="check"
          onClick={() => navigate('/attendance')}
          foot={<Badge tone={present > active * 0.9 ? 'success' : 'warn'} dot>{present > active * 0.9 ? 'ممتاز' : 'يحتاج متابعة'}</Badge>}
        />
        <ClickableKPI
          label="الغياب"
          value={fmtAR(absent)}
          sub="بإذن وبدون إذن"
          icon="x"
          onClick={() => navigate('/attendance')}
          foot={<Badge tone={absent < 5 ? 'success' : 'warn'} dot>{absent < 5 ? 'منخفض' : 'مرتفع'}</Badge>}
        />
        <ClickableKPI
          label="التأخير"
          value={fmtAR(late)}
          sub="بصمات بعد الموعد"
          icon="clock"
          onClick={() => navigate('/attendance')}
          foot={missing > 0 ? <Badge tone="danger" dot>{fmtAR(missing)} بصمة ناقصة</Badge> : <Badge tone="success" dot>كلها مكتملة</Badge>}
        />
        <ClickableKPI
          label="كفاءة الإنتاج"
          value={`${prodEff}%`}
          sub={prod ? `${fmtAR(prod.totalProduced)} / ${fmtAR(prod.totalTarget)}` : '—'}
          icon="factory"
          onClick={() => navigate('/production/workers')}
          foot={<Badge tone={prodEff >= 100 ? 'success' : 'warn'} dot>{prodEff >= 100 ? 'فوق الهدف' : 'تحت الهدف'}</Badge>}
        />
        <ClickableKPI
          label="رواتب الشهر"
          value={payroll ? `${(totalPayroll / 1e6).toFixed(2)}M` : '—'}
          sub={payroll ? `EGP · ${fmtAR(payroll.employeeCount)} موظف` : 'لم تُشغَّل بعد'}
          icon="wallet"
          accent
          onClick={() => navigate('/payroll')}
          foot={
            payroll ? (
              <Badge tone={payroll.status === 'approved' ? 'success' : 'warn'} dot>
                {payroll.status === 'approved' ? 'معتمد' : 'تحت المراجعة'}
              </Badge>
            ) : (
              <Badge tone="neutral">لم يُنفَّذ</Badge>
            )
          }
        />
      </div>

      {/* ─── Advances KPIs ─── */}
      {dash.advancesKpi && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
                السلف والقروض
              </div>
              <h3 className="font-bold mt-0.5">حالة السلف المالية</h3>
            </div>
            <button
              onClick={() => navigate('/uploads')}
              className="text-[11px] text-brand-500 font-bold hover:underline"
            >
              رفع/استيراد سلف ←
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 ring-1 ring-amber-200 dark:ring-amber-800">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300 uppercase">
                  سلف نشطة (متبقى السداد)
                </span>
                <Icon name="briefcase" size={16} className="text-amber-600" />
              </div>
              <div className="text-2xl font-black tabular text-amber-700 dark:text-amber-300">
                {fmtMoney(dash.advancesKpi.outstandingTotal)}
              </div>
              <div className="text-[11px] text-ink-400 mt-1">إجمالي ما لم يُسدَّد بعد</div>
            </div>

            <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200 dark:ring-rose-800">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-rose-700 dark:text-rose-300 uppercase">
                  تم خصمه هذا الشهر
                </span>
                <Icon name="trendD" size={16} className="text-rose-600" />
              </div>
              <div className="text-2xl font-black tabular text-rose-700 dark:text-rose-300">
                {fmtMoney(dash.advancesKpi.deductedThisMonth)}
              </div>
              <div className="text-[11px] text-ink-400 mt-1">من رواتب الموظفين</div>
            </div>

            <div className="p-4 rounded-2xl bg-sky-50 dark:bg-sky-950/30 ring-1 ring-sky-200 dark:ring-sky-800">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-sky-700 dark:text-sky-300 uppercase">
                  سلف صُرفت هذا الشهر
                </span>
                <Icon name="plus" size={16} className="text-sky-600" />
              </div>
              <div className="text-2xl font-black tabular text-sky-700 dark:text-sky-300">
                {fmtMoney(dash.advancesKpi.newThisMonthAmount)}
              </div>
              <div className="text-[11px] text-ink-400 mt-1">
                {fmtAR(dash.advancesKpi.newThisMonthCount)} سلفة جديدة
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ─── Charts row ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attendance trend (last 14 days) */}
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400 font-bold">
                آخر 14 يوم
              </div>
              <h3 className="font-bold mt-0.5">حركة الحضور</h3>
            </div>
            <button
              onClick={() => navigate('/attendance')}
              className="text-[11px] text-brand-500 font-bold hover:underline"
            >
              عرض الكل ←
            </button>
          </div>
          <div className="text-ink-700 dark:text-ink-200">
            <LineChart
              data={(trend?.trend ?? []).map((t) => ({
                label: t.date.slice(8),
                value: t.present,
              }))}
              color="#cc9c46"
              height={200}
            />
          </div>
        </Card>

        {/* Department distribution donut */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400 font-bold">
                توزيع الأقسام
              </div>
              <h3 className="font-bold mt-0.5">نسبة الموظفين</h3>
            </div>
          </div>
          <div className="text-ink-700 dark:text-ink-200 text-center">
            {byDept && byDept.rows.length > 0 ? (
              <DonutChart
                size={180}
                thickness={28}
                centerLabel="موظف"
                centerValue={fmtAR(total)}
                data={byDept.rows
                  .filter((d) => d.employeeCount > 0)
                  .slice(0, 8)
                  .map((d) => ({
                    label: d.deptAr,
                    value: d.employeeCount,
                    color: d.color,
                  }))}
              />
            ) : (
              <div className="py-10 text-ink-400 text-sm">لا توجد بيانات</div>
            )}
          </div>
        </Card>
      </div>

      {/* ─── Production section (admin overview) ─── */}
      {eng && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
                نظرة الإنتاج
              </div>
              <h3 className="font-bold text-base mt-0.5">أداء الإنتاج هذا الشهر</h3>
            </div>
            <button
              onClick={() => navigate('/production/workers')}
              className="text-[11px] text-brand-500 font-bold hover:underline"
            >
              عرض العمال ←
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MiniCard label="عمال نشطون" value={fmtAR(eng.kpis.totalWorkers)} icon="users" />
            <MiniCard
              label="إنتاج الشهر"
              value={fmtAR(eng.kpis.totalProduced)}
              sub={`الهدف ${fmtAR(eng.kpis.totalTarget)}`}
              icon="factory"
            />
            <MiniCard
              label="حقّقوا اليوم"
              value={fmtAR(eng.kpis.hitsToday)}
              tone="success"
              icon="trendU"
            />
            <MiniCard
              label="تحت الهدف اليوم"
              value={fmtAR(eng.kpis.missesToday)}
              tone={eng.kpis.missesToday > 0 ? 'warn' : 'success'}
              icon="trendD"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {(eng.stages ?? []).map((s) => (
              <button
                key={s.stageId}
                onClick={() => navigate(`/production/workers?stage=${s.stageId}`)}
                className="text-start p-3 rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 bg-white dark:bg-ink-900 hover:ring-brand-400 transition"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="inline-flex items-center gap-1.5 font-bold text-sm">
                    <span
                      className="w-2.5 h-2.5 rounded"
                      style={{ backgroundColor: s.color }}
                    />
                    {s.nameAr}
                  </span>
                  <Badge tone={s.efficiency >= 100 ? 'success' : 'warn'}>{s.efficiency}%</Badge>
                </div>
                <div className="text-lg font-extrabold tabular">{fmtAR(s.produced)}</div>
                <div className="text-[10px] text-ink-400">
                  {fmtAR(s.workerCount)} عامل · هدف {fmtAR(s.target)}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'صباح الخير';
  if (h < 17) return 'مساء الخير';
  return 'مساء الخير';
}

function ClickableKPI({ label, value, sub, icon, accent, foot, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-start group rounded-2xl bg-white dark:bg-ink-900 ring-1 ring-ink-100 dark:ring-ink-800 shadow-soft p-4 hover:ring-brand-400 transition cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-ink-400 font-bold">
            {label}
          </div>
          <div className="text-2xl md:text-3xl font-black mt-1 tabular truncate">{value}</div>
          {sub && <div className="text-xs text-ink-400 mt-0.5 line-clamp-1">{sub}</div>}
        </div>
        {icon && (
          <span
            className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 transition group-hover:scale-105 ${
              accent
                ? 'bg-brand-500 text-ink-950'
                : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300'
            }`}
          >
            <Icon name={icon} size={18} />
          </span>
        )}
      </div>
      {foot && (
        <div className="mt-3 pt-3 border-t border-ink-100 dark:border-ink-800">{foot}</div>
      )}
    </button>
  );
}

function CostCard({ label, value, sub, icon, tone = 'brand' }) {
  const tones = {
    brand: 'bg-gradient-to-bl from-brand-500/15 to-brand-50 dark:to-brand-950/30 text-brand-700 dark:text-brand-200 ring-brand-200 dark:ring-brand-800',
    dark: 'bg-gradient-to-bl from-ink-700/10 to-ink-50 dark:to-ink-900 text-ink-800 dark:text-ink-100 ring-ink-200 dark:ring-ink-700',
  };
  return (
    <div className={`p-5 rounded-2xl ring-1 shadow-soft ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold opacity-80">
            {label}
          </div>
          <div className="text-3xl md:text-4xl font-black tabular mt-1">
            {fmtMoney(value)}
          </div>
          {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
        </div>
        {icon && (
          <span className="w-12 h-12 rounded-2xl bg-white/50 dark:bg-ink-900/40 grid place-items-center shrink-0">
            <Icon name={icon} size={22} />
          </span>
        )}
      </div>
    </div>
  );
}

function MiniCard({ label, value, sub, icon, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    success: 'text-emerald-600',
    warn: 'text-amber-600',
    info: 'text-sky-600',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-xl font-black tabular mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}
